import React, { useState } from 'react';
import { BlogArticle } from '../../types/blog';
import { getRelatedArticles } from '../../data/blog';
import { BlogCard } from './BlogCard';

export function formatMathText(raw: string): string {
  if (!raw) return '';
  let s = raw.trim();

  // Normalize malformed text escapes (ext{...}, \text{...}, \t ext)
  s = s.replace(/[\t]ext\{([^{}]+)\}/g, '$1');
  s = s.replace(/(?:\\text|\bext)\{([^{}]+)\}/g, '$1');

  // Temperatures and degree symbols
  s = s.replace(/\^\\circ\s*\\?text\{?([CF])\}?/g, '°$1');
  s = s.replace(/\^\\circ\s*([CF])/g, '°$1');
  s = s.replace(/\^\\circ/g, '°');

  // Fractions (nested up to 4 levels)
  for (let i = 0; i < 4; i++) {
    s = s.replace(/\\frac\{([^{}]+)\}\{([^{}]+)\}/g, '($1) / ($2)');
  }

  // Square roots (nested up to 4 levels)
  for (let i = 0; i < 4; i++) {
    s = s.replace(/\\sqrt\{([^{}]+)\}/g, '√($1)');
  }

  // Greek letters
  s = s.replace(/\\rho/g, 'ρ');
  s = s.replace(/\\sigma/g, 'σ');
  s = s.replace(/\\mu/g, 'µ');
  s = s.replace(/\\nu/g, 'ν');
  s = s.replace(/\\pi/g, 'π');
  s = s.replace(/\\epsilon/g, 'ε');
  s = s.replace(/\\Delta/g, 'Δ');
  s = s.replace(/\\delta/g, 'δ');
  s = s.replace(/\\tau/g, 'τ');
  s = s.replace(/\\lambda/g, 'λ');
  s = s.replace(/\\alpha/g, 'α');
  s = s.replace(/\\beta/g, 'β');
  s = s.replace(/\\gamma/g, 'γ');
  s = s.replace(/\\theta/g, 'θ');
  s = s.replace(/\\phi/g, 'φ');
  s = s.replace(/\\omega/g, 'ω');
  s = s.replace(/\\Omega/g, 'Ω');

  // Operators and standard mathematical symbols
  s = s.replace(/\\dot\{m\}|\\dot m/g, 'ṁ');
  s = s.replace(/\\cdot/g, ' · ');
  s = s.replace(/\\times/g, ' × ');
  s = s.replace(/\\approx/g, ' ≈ ');
  s = s.replace(/\\le(q)?\b/g, ' ≤ ');
  s = s.replace(/\\ge(q)?\b/g, ' ≥ ');
  s = s.replace(/\\ne(q)?\b/g, ' ≠ ');
  s = s.replace(/\\pm/g, ' ± ');
  s = s.replace(/\\implies/g, ' ⇒ ');
  s = s.replace(/\\quad/g, '  ');
  s = s.replace(/\\qquad/g, '    ');
  s = s.replace(/\\dots|\\cdots/g, '…');

  // Parentheses & brackets
  s = s.replace(/\\left\(/g, '(').replace(/\\right\)/g, ')');
  s = s.replace(/\\left\[/g, '[').replace(/\\right\]/g, ']');
  s = s.replace(/\\left\\\{/g, '{').replace(/\\right\\\}/g, '}');
  s = s.replace(/\\\\/g, '\n');

  // Superscripts
  s = s.replace(/\^2\b/g, '²');
  s = s.replace(/\^3\b/g, '³');
  s = s.replace(/\^4\b/g, '⁴');
  s = s.replace(/\^5\b/g, '⁵');
  s = s.replace(/\^\{(-?[0-9]+)\}/g, (_, exp) => {
    const map: Record<string, string> = {
      '-': '⁻', '0': '⁰', '1': '¹', '2': '²', '3': '³', '4': '⁴',
      '5': '⁵', '6': '⁶', '7': '⁷', '8': '⁸', '9': '⁹'
    };
    return exp.split('').map((c: string) => map[c] || c).join('');
  });
  s = s.replace(/\^\{([^{}]+)\}/g, '^($1)');

  // Subscripts
  s = s.replace(/_([0-9])/g, (_, d) => {
    const map: Record<string, string> = {
      '0': '₀', '1': '₁', '2': '₂', '3': '₃', '4': '₄',
      '5': '₅', '6': '₆', '7': '₇', '8': '₈', '9': '₉'
    };
    return map[d] || '_' + d;
  });
  s = s.replace(/_\{([^{}]+)\}/g, '_$1');

  // Clean remaining stray backslashes before words
  s = s.replace(/\\([a-zA-Z]+)/g, '$1');

  // Normalize duplicate spaces
  s = s.replace(/ {2,}/g, ' ').trim();

  return s;
}

export function renderInlineContent(text: string): React.ReactNode {
  if (!text) return null;

  // Split by inline math ($...$), bold (**...**), and code (`...`)
  const tokenRegex = /(\$\$[\s\S]*?\$\$|\$[^$\n]+?\$|\*\*[^*]+?\*\*|`[^`]+`)/g;
  const parts = text.split(tokenRegex);

  return parts.map((part, index) => {
    if (!part) return null;

    if (part.startsWith('$$') && part.endsWith('$$')) {
      const math = part.slice(2, -2);
      return (
        <span
          key={index}
          className="block my-2.5 p-3 rounded-xl bg-slate-50 dark:bg-slate-800/80 border border-slate-200 dark:border-slate-700 font-mono text-xs sm:text-sm text-center font-bold text-[#10283D] dark:text-[#39BFD3] overflow-x-auto whitespace-pre-wrap"
        >
          {formatMathText(math)}
        </span>
      );
    }

    if (part.startsWith('$') && part.endsWith('$')) {
      const math = part.slice(1, -1);
      return (
        <span
          key={index}
          className="font-mono text-xs font-semibold text-[#10283D] dark:text-[#39BFD3] px-1 py-0.5 rounded bg-slate-100 dark:bg-slate-800/80 border border-slate-200/60 dark:border-slate-700/60 inline-block my-0.5"
        >
          {formatMathText(math)}
        </span>
      );
    }

    if (part.startsWith('**') && part.endsWith('**')) {
      const inner = part.slice(2, -2);
      return (
        <strong key={index} className="font-semibold text-slate-900 dark:text-white">
          {renderInlineContent(inner)}
        </strong>
      );
    }

    if (part.startsWith('`') && part.endsWith('`')) {
      return (
        <code
          key={index}
          className="font-mono text-xs px-1.5 py-0.5 rounded bg-slate-100 dark:bg-slate-800 text-slate-800 dark:text-slate-200"
        >
          {part.slice(1, -1)}
        </code>
      );
    }

    // Clean plain text: handle degree symbols, stray \text or ext
    let cleaned = part;
    cleaned = cleaned.replace(/[\t]ext\{([^{}]+)\}/g, '$1');
    cleaned = cleaned.replace(/(?:\\text|\bext)\{([^{}]+)\}/g, '$1');
    cleaned = cleaned.replace(/\^\\circ\s*\\?text\{?([CF])\}?/g, '°$1');
    cleaned = cleaned.replace(/\^\\circ\s*([CF])/g, '°$1');
    cleaned = cleaned.replace(/\^\\circ/g, '°');

    return <React.Fragment key={index}>{cleaned}</React.Fragment>;
  });
}

interface BlogArticlePageProps {
  article: BlogArticle;
  onNavigate: (path: string, view: string, tab?: string) => void;
  isStatic?: boolean;
}

export const BlogArticlePage: React.FC<BlogArticlePageProps> = ({
  article,
  onNavigate,
  isStatic,
}) => {
  const [openFaqIndex, setOpenFaqIndex] = useState<number | null>(null);
  const [copiedLink, setCopiedLink] = useState(false);

  const relatedArticles = getRelatedArticles(article.slug, 3);

  const toggleFaq = (index: number) => {
    setOpenFaqIndex((current) => (current === index ? null : index));
  };

  const handleCopyLink = () => {
    if (typeof window !== 'undefined') {
      navigator.clipboard.writeText(window.location.href);
      setCopiedLink(true);
      setTimeout(() => setCopiedLink(false), 2000);
    }
  };

  return (
    <div className="min-h-screen bg-[#F1F6F9] dark:bg-[#111827] text-slate-800 dark:text-slate-100 transition-colors py-8 sm:py-12">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 space-y-8">
        {/* Breadcrumb Navigation */}
        <nav
          aria-label="Breadcrumb"
          className="flex items-center flex-wrap gap-2 text-xs text-slate-500 dark:text-slate-400 font-medium"
        >
          <a
            href="/"
            onClick={(e) => {
              e.preventDefault();
              onNavigate('/', 'calculators', 'water');
            }}
            className="hover:text-[#087F9C] dark:hover:text-[#39BFD3] transition-colors"
          >
            Home
          </a>
          <span>/</span>
          <a
            href="/blog/"
            onClick={(e) => {
              e.preventDefault();
              onNavigate('/blog/', 'blog-home');
            }}
            className="hover:text-[#087F9C] dark:hover:text-[#39BFD3] transition-colors"
          >
            Engineering Blog
          </a>
          <span>/</span>
          <span className="text-slate-400 dark:text-slate-500 truncate max-w-[150px] sm:max-w-xs">
            {article.category}
          </span>
          <span>/</span>
          <span className="text-[#087F9C] dark:text-[#39BFD3] font-semibold truncate max-w-[160px] sm:max-w-xs">
            {article.h1}
          </span>
        </nav>

        {/* Main Article Container */}
        <article className="bg-white dark:bg-[#182332] rounded-3xl p-6 sm:p-10 lg:p-12 space-y-8 border border-slate-200 dark:border-slate-700 shadow-sm">
          {/* Article Header */}
          <header className="border-b border-slate-100 dark:border-slate-700 pb-6 space-y-4">
            <div className="flex flex-wrap items-center justify-between gap-3">
              <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-bold uppercase tracking-wider bg-teal-50 dark:bg-teal-950/60 text-[#087F9C] dark:text-[#39BFD3] border border-teal-200/60 dark:border-teal-800">
                {article.category}
              </span>

              <div className="flex items-center gap-3 text-xs text-slate-400">
                <span>{article.readingTime}</span>
                <span>•</span>
                <span>Updated: {article.date}</span>
              </div>
            </div>

            <h1 className="font-display font-extrabold text-2xl sm:text-3xl lg:text-4xl text-[#10283D] dark:text-white tracking-tight leading-tight">
              {article.h1}
            </h1>

            {/* Author / Credential Tag */}
            <div className="flex items-center justify-between flex-wrap gap-4 pt-2 text-xs text-slate-500 dark:text-slate-400">
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 rounded-full bg-[#10283D] text-[#39BFD3] flex items-center justify-center font-bold text-xs">
                  P
                </div>
                <div>
                  <span className="font-semibold text-slate-700 dark:text-slate-200 block">
                    {article.author.name}
                  </span>
                  <span className="text-[11px] text-slate-400">
                    {article.author.title}
                  </span>
                </div>
              </div>

              <button
                type="button"
                onClick={handleCopyLink}
                className="px-3 py-1.5 rounded-lg border border-slate-200 dark:border-slate-700 hover:bg-slate-50 dark:hover:bg-slate-800 text-[11px] font-semibold text-slate-600 dark:text-slate-300 flex items-center gap-1.5 transition-colors"
              >
                <span>{copiedLink ? '✓ Copied!' : '🔗 Share Link'}</span>
              </button>
            </div>
          </header>

          {/* Lead Paragraph (AEO Optimized Direct Answer) */}
          <div className="p-4 sm:p-5 rounded-2xl bg-teal-50/50 dark:bg-teal-950/30 border border-teal-100 dark:border-teal-900/40 text-sm sm:text-base text-slate-700 dark:text-slate-200 font-medium leading-relaxed">
            {renderInlineContent(article.leadParagraph)}
          </div>

          {/* Table of Contents */}
          {article.tableOfContents && article.tableOfContents.length > 0 && (
            <div className="p-5 rounded-2xl bg-slate-50 dark:bg-slate-800/60 border border-slate-200 dark:border-slate-700 space-y-3">
              <h2 className="text-xs font-bold uppercase tracking-wider text-slate-400 dark:text-slate-500 flex items-center gap-1.5">
                <span>📑</span> Table of Contents
              </h2>
              <ol className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-xs font-semibold text-[#087F9C] dark:text-[#39BFD3]">
                {article.tableOfContents.map((item) => (
                  <li key={item.id}>
                    <a
                      href={`#${item.id}`}
                      className="hover:underline flex items-center gap-1"
                    >
                      <span>•</span>
                      <span className="truncate">{renderInlineContent(item.title)}</span>
                    </a>
                  </li>
                ))}
              </ol>
            </div>
          )}

          {/* Dedicated Custom 2D Engineering Visual */}
          <figure className="space-y-3 my-6">
            <div className="rounded-2xl overflow-hidden border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 shadow-sm p-3 sm:p-4">
              <img
                src={`/assets/blog/${article.svgVisualId}.svg`}
                alt={article.svgAlt}
                className="w-full h-auto max-h-[480px] object-contain mx-auto"
              />
            </div>
            <figcaption className="text-xs text-center text-slate-500 dark:text-slate-400 font-medium italic">
              {article.svgCaption}
            </figcaption>
          </figure>

          {/* Structured Content Sections */}
          <div className="space-y-10 text-slate-700 dark:text-slate-200 text-sm sm:text-base leading-relaxed">
            {article.sections.map((sec) => (
              <section key={sec.id} id={sec.id} className="space-y-4 pt-2">
                <h2 className="font-display font-bold text-xl sm:text-2xl text-[#10283D] dark:text-white tracking-tight border-b border-slate-100 dark:border-slate-700/60 pb-2">
                  {renderInlineContent(sec.h2)}
                </h2>

                {/* Main section text (split paragraphs) */}
                <div className="space-y-3 text-sm sm:text-base leading-relaxed">
                  {sec.content.split('\n\n').map((para, i) => {
                    const trimmed = para.trim();

                    // Markdown H3 Subheading
                    if (trimmed.startsWith('### ')) {
                      return (
                        <h3
                          key={i}
                          className="font-display font-bold text-base sm:text-lg text-[#10283D] dark:text-white pt-4 pb-1"
                        >
                          {renderInlineContent(trimmed.replace(/^###\s+/, ''))}
                        </h3>
                      );
                    }

                    // Display math block ($$...$$)
                    if (trimmed.startsWith('$$') && trimmed.endsWith('$$')) {
                      return (
                        <div
                          key={i}
                          className="my-3 p-3.5 rounded-xl bg-slate-50 dark:bg-slate-800/80 border border-slate-200 dark:border-slate-700 font-mono text-xs sm:text-sm text-center font-bold text-[#10283D] dark:text-[#39BFD3] overflow-x-auto whitespace-pre-wrap"
                        >
                          {formatMathText(trimmed.replace(/^\$\$|\$\$$/g, ''))}
                        </div>
                      );
                    }

                    // Multi-line list items or paragraph with breaks
                    if (para.includes('\n')) {
                      const lines = para.split('\n');
                      return (
                        <div key={i} className="space-y-1.5 leading-relaxed">
                          {lines.map((line, li) => {
                            const trimmedLine = line.trim();
                            if (trimmedLine.startsWith('### ')) {
                              return (
                                <h3
                                  key={li}
                                  className="font-display font-bold text-base sm:text-lg text-[#10283D] dark:text-white pt-4 pb-1"
                                >
                                  {renderInlineContent(trimmedLine.replace(/^###\s+/, ''))}
                                </h3>
                              );
                            }
                            if (trimmedLine.startsWith('$$') && trimmedLine.endsWith('$$')) {
                              return (
                                <div
                                  key={li}
                                  className="my-3 p-3.5 rounded-xl bg-slate-50 dark:bg-slate-800/80 border border-slate-200 dark:border-slate-700 font-mono text-xs sm:text-sm text-center font-bold text-[#10283D] dark:text-[#39BFD3] overflow-x-auto whitespace-pre-wrap"
                                >
                                  {formatMathText(trimmedLine.replace(/^\$\$|\$\$$/g, ''))}
                                </div>
                              );
                            }
                            if (trimmedLine.startsWith('- ') || trimmedLine.startsWith('* ')) {
                              return (
                                <div key={li} className="flex items-start gap-2 pl-2">
                                  <span className="text-teal-600 dark:text-teal-400 font-bold select-none">•</span>
                                  <span>{renderInlineContent(trimmedLine.replace(/^[-*]\s+/, ''))}</span>
                                </div>
                              );
                            }
                            const numberedMatch = trimmedLine.match(/^(\d+\.)\s+(.*)$/);
                            if (numberedMatch) {
                              return (
                                <div key={li} className="flex items-start gap-2 pl-2">
                                  <span className="font-semibold text-slate-500 dark:text-slate-400 select-none shrink-0">{numberedMatch[1]}</span>
                                  <span>{renderInlineContent(numberedMatch[2])}</span>
                                </div>
                              );
                            }
                            return (
                              <p key={li} className="leading-relaxed">
                                {renderInlineContent(line)}
                              </p>
                            );
                          })}
                        </div>
                      );
                    }

                    return (
                      <p key={i} className="leading-relaxed">
                        {renderInlineContent(para)}
                      </p>
                    );
                  })}
                </div>

                {/* Subsections if present */}
                {sec.subsections && sec.subsections.length > 0 && (
                  <div className="space-y-4 pt-2 pl-3 border-l-2 border-teal-500/30">
                    {sec.subsections.map((sub, j) => (
                      <div key={j} className="space-y-2">
                        <h3 className="font-display font-bold text-base sm:text-lg text-[#10283D] dark:text-white">
                          {renderInlineContent(sub.h3)}
                        </h3>
                        <p className="text-sm leading-relaxed">{renderInlineContent(sub.content)}</p>
                      </div>
                    ))}
                  </div>
                )}

                {/* Callout Box */}
                {sec.calloutBox && (
                  <div className="my-4 p-4 rounded-2xl bg-amber-50 dark:bg-amber-950/40 border border-amber-200 dark:border-amber-800 text-amber-900 dark:text-amber-200 text-xs sm:text-sm space-y-1">
                    <span className="font-bold flex items-center gap-1.5">
                      <span>💡</span> {renderInlineContent(sec.calloutBox.title)}
                    </span>
                    <p className="leading-relaxed text-amber-800 dark:text-amber-300">
                      {renderInlineContent(sec.calloutBox.text)}
                    </p>
                  </div>
                )}

                {/* Comparison Table */}
                {sec.table && (
                  <div className="my-4 overflow-x-auto rounded-2xl border border-slate-200 dark:border-slate-700">
                    <table className="min-w-full text-left text-xs divide-y divide-slate-200 dark:divide-slate-700">
                      {sec.table.caption && (
                        <caption className="p-3 text-xs font-bold text-slate-600 dark:text-slate-300 bg-slate-50 dark:bg-slate-800/80 text-left border-b border-slate-200 dark:border-slate-700">
                          {renderInlineContent(sec.table.caption)}
                        </caption>
                      )}
                      <thead className="bg-slate-100 dark:bg-slate-800 font-bold text-[#10283D] dark:text-white">
                        <tr>
                          {sec.table.headers.map((h, hi) => (
                            <th key={hi} className="p-3 whitespace-nowrap">
                              {renderInlineContent(h)}
                            </th>
                          ))}
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-slate-100 dark:divide-slate-800 bg-white dark:bg-[#182332]">
                        {sec.table.rows.map((row, ri) => (
                          <tr
                            key={ri}
                            className="hover:bg-slate-50 dark:hover:bg-slate-800/40 transition-colors"
                          >
                            {row.map((cell, ci) => (
                              <td
                                key={ci}
                                className={`p-3 whitespace-nowrap ${
                                  ci === 0
                                    ? 'font-semibold text-slate-900 dark:text-white'
                                    : 'text-slate-600 dark:text-slate-300 font-mono text-[11px]'
                                }`}
                              >
                                {typeof cell === 'string' ? renderInlineContent(cell) : cell}
                              </td>
                            ))}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}

                {/* Worked Calculation Example */}
                {sec.workedExample && (
                  <div className="my-6 p-5 sm:p-6 rounded-2xl bg-slate-50 dark:bg-slate-800/80 border border-slate-200 dark:border-slate-700 space-y-4">
                    <div className="flex items-center gap-2 text-xs font-bold uppercase tracking-wider text-[#087F9C] dark:text-[#39BFD3]">
                      <span>🧮</span>
                      <span>{renderInlineContent(sec.workedExample.title)}</span>
                    </div>

                    <p className="text-sm font-semibold text-slate-800 dark:text-slate-100">
                      {renderInlineContent(sec.workedExample.problem)}
                    </p>

                    {/* Given Parameters */}
                    <div className="p-3 rounded-xl bg-white dark:bg-[#10283D] border border-slate-200 dark:border-slate-700 text-xs font-mono space-y-1">
                      <span className="font-bold text-slate-500 uppercase tracking-wider block">
                        Given Parameters:
                      </span>
                      {sec.workedExample.given.map((g, gi) => (
                        <div key={gi} className="text-slate-700 dark:text-slate-200">
                          • {renderInlineContent(g)}
                        </div>
                      ))}
                    </div>

                    {/* Calculation Steps */}
                    <div className="space-y-3">
                      {sec.workedExample.steps.map((st) => (
                        <div
                          key={st.stepNumber}
                          className="text-xs sm:text-sm space-y-1"
                        >
                          <span className="font-bold text-[#10283D] dark:text-white">
                            Step {st.stepNumber}: {renderInlineContent(st.description)}
                          </span>
                          {st.math && (
                            <div className="p-2.5 rounded-lg bg-slate-100 dark:bg-slate-900 font-mono text-xs overflow-x-auto text-[#087F9C] dark:text-[#39BFD3] whitespace-pre-wrap">
                              {formatMathText(st.math)}
                            </div>
                          )}
                          {st.result && (
                            <div className="text-xs font-semibold text-emerald-600 dark:text-emerald-400 font-mono">
                              &rArr; {formatMathText(st.result)}
                            </div>
                          )}
                        </div>
                      ))}
                    </div>

                    <div className="p-3 rounded-xl bg-teal-50 dark:bg-teal-950/60 border border-teal-200 dark:border-teal-800 text-xs text-teal-900 dark:text-teal-200 font-medium">
                      <strong>Conclusion:</strong> {renderInlineContent(sec.workedExample.conclusion)}
                    </div>
                  </div>
                )}
              </section>
            ))}
          </div>

          {/* Contextual Calculator CTA Card */}
          <div className="my-8 p-6 sm:p-8 rounded-3xl bg-gradient-to-r from-[#10283D] to-[#087F9C] text-white shadow-md flex flex-col sm:flex-row sm:items-center sm:justify-between gap-6">
            <div className="space-y-1 max-w-xl">
              <span className="text-[11px] font-bold uppercase tracking-wider text-[#39BFD3] block">
                Interactive Engineering Tool
              </span>
              <p className="font-display font-extrabold text-xl text-white">
                {article.calculatorName}
              </p>
              <p className="text-xs text-slate-200 leading-relaxed">
                Run exact calculations using our peer-reviewed fluid mechanics engine. Includes metric/imperial unit conversions and downloadable PDF compliance reports.
              </p>
            </div>

            <a
              href={article.calculatorLink}
              onClick={(e) => {
                e.preventDefault();
                onNavigate(article.calculatorLink, 'calculators', 'water');
              }}
              className="px-5 py-3 rounded-2xl font-bold text-xs bg-white text-[#10283D] hover:bg-slate-100 shadow-md transition-all duration-200 whitespace-nowrap shrink-0"
            >
              ⚡ {article.calculatorCtaText} &rarr;
            </a>
          </div>

          {/* Frequently Asked Questions (FAQ Section) */}
          <section id="faqs" className="pt-6 border-t border-slate-100 dark:border-slate-700 space-y-4">
            <div className="flex items-center gap-2">
              <span className="text-xl">❓</span>
              <h2 className="font-display font-bold text-xl sm:text-2xl text-[#10283D] dark:text-white">
                Frequently Asked Questions
              </h2>
            </div>

            <div className="space-y-3 pt-2">
              {article.faqs.map((faq, fi) => {
                const isOpen = openFaqIndex === fi;
                if (isStatic) {
                  return (
                    <div
                      key={fi}
                      className="rounded-2xl border border-slate-200 dark:border-slate-700 bg-slate-50/50 dark:bg-slate-800/40 p-4 space-y-2"
                    >
                      <h3 className="font-bold text-sm text-[#10283D] dark:text-white">
                        {renderInlineContent(faq.question)}
                      </h3>
                      <div className="text-xs sm:text-sm text-slate-600 dark:text-slate-300 leading-relaxed">
                        {renderInlineContent(faq.answer)}
                      </div>
                    </div>
                  );
                }
                return (
                  <div
                    key={fi}
                    className="rounded-2xl border border-slate-200 dark:border-slate-700 bg-slate-50/50 dark:bg-slate-800/40 overflow-hidden transition-colors"
                  >
                    <button
                      type="button"
                      onClick={() => toggleFaq(fi)}
                      className="w-full p-4 text-left font-bold text-sm text-[#10283D] dark:text-white flex items-center justify-between gap-3"
                    >
                      <h3 className="font-bold text-sm text-[#10283D] dark:text-white text-left">
                        {renderInlineContent(faq.question)}
                      </h3>
                      <span className="text-xs font-mono text-[#087F9C] dark:text-[#39BFD3] shrink-0">
                        {isOpen ? '▲' : '▼'}
                      </span>
                    </button>
                    {isOpen && (
                      <div className="px-4 pb-4 text-xs sm:text-sm text-slate-600 dark:text-slate-300 leading-relaxed border-t border-slate-100 dark:border-slate-700/60 pt-3">
                        {renderInlineContent(faq.answer)}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </section>

          {/* Peer-Reviewed Engineering Review Notice */}
          <footer className="pt-6 border-t border-slate-100 dark:border-slate-700 text-xs text-slate-500 dark:text-slate-400 space-y-2">
            <div className="flex items-center gap-2">
              <span className="w-2 h-2 rounded-full bg-emerald-500" />
              <span>
                <strong>Engineering Review:</strong> Verified against BS EN 806, BS EN 1057, ASME B36.10M, ASTM B88, and CIBSE Guide G.
              </span>
            </div>
            <p className="text-[11px] leading-relaxed">
              Calculation formulas and reference tables are maintained for professional mechanical designers. For specialized project-specific installation warranties, verify local authority plumbing codes.
            </p>
          </footer>
        </article>

        {/* Related Articles Section */}
        {relatedArticles.length > 0 && (
          <section className="space-y-6 pt-4">
            <div className="flex items-center justify-between">
              <h2 className="font-display font-bold text-xl sm:text-2xl text-[#10283D] dark:text-white">
                Related Engineering Guides
              </h2>
              <a
                href="/blog/"
                onClick={(e) => {
                  e.preventDefault();
                  onNavigate('/blog/', 'blog-home');
                }}
                className="text-xs font-bold text-[#087F9C] dark:text-[#39BFD3] hover:underline"
              >
                View all 30 guides &rarr;
              </a>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {relatedArticles.map((rel) => (
                <BlogCard key={rel.slug} article={rel} onNavigate={onNavigate} headingLevel="h3" />
              ))}
            </div>
          </section>
        )}

        {/* Back to Blog Hub Button */}
        <div className="pt-4 text-center">
          <a
            href="/blog/"
            onClick={(e) => {
              e.preventDefault();
              onNavigate('/blog/', 'blog-home');
            }}
            className="inline-block px-6 py-3 rounded-2xl text-xs font-bold bg-white dark:bg-[#182332] text-[#10283D] dark:text-white border border-slate-200 dark:border-slate-700 hover:bg-slate-50 dark:hover:bg-slate-800 shadow-sm transition-all"
          >
            &larr; Return to Engineering Knowledge Hub
          </a>
        </div>
      </div>
    </div>
  );
};
